import { parseDateField } from '@/utils/formatters';
import { businessDaysBetween } from '@/utils/businessDays';

/**
 * Calcola i KPI principali basati sul nuovo modello dati (workflowStatus).
 *
 * @param {Array} practices - Array di pratiche
 * @returns {Object} KPI calcolati
 */
export function computeKpis(practices) {
  const nonAvviate = practices.filter((p) => p.workflowStatus === 'non_avviato');
  // Card Dashboard "Attività In Corso": include TUTTI gli stati attivi (pratica viva non
  // ancora chiusa): in_corso + approvazione_hq + rifiutato + rifiutato_ditta. In precedenza
  // contava solo il letterale 'in_corso', lasciando un buco di visibilità su approvazione_hq
  // e sui rifiuti, e azzerava la card per la Ditta (che non vede mai 'in_corso' per rules).
  const ACTIVE_STATES = new Set(['in_corso', 'approvazione_hq', 'rifiutato', 'rifiutato_ditta']);
  const inCorso = practices.filter((p) => ACTIVE_STATES.has(p.workflowStatus));
  const chiuse = practices.filter((p) => p.workflowStatus === 'chiuso');
  const rifiutate = practices.filter((p) => p.workflowStatus === 'rifiutato');
  // Coda di lavoro della Ditta: pratiche che deve decidere ora (preventivo da valutare).
  const daValutare = practices.filter((p) => p.workflowStatus === 'approvazione_hq');
  // Pratiche che la Ditta ha rifiutato e aspettano la nuova versione del fornitore.
  const inRielaborazione = practices.filter((p) => p.workflowStatus === 'rifiutato_ditta');

  // Lead time medio = tempo di lavorazione documenti espresso in giorni lavorativi
  // (esclude weekend e festività nazionali italiane, incluse Pasquetta mobile).
  // Intervallo: dataInizio (fornitore clicca "doc caricati") → dataApprovazioneDocumenti
  // (il controllore approva i documenti). Considera tutte le pratiche passate almeno
  // dall'approvazione documenti, indipendentemente dallo stato finale.
  const leadTimes = practices
    .filter((p) => p.dataApprovazioneDocumenti)
    .map((p) => {
      const inizio = parseDateField(p.dataInizio);
      const approvato = parseDateField(p.dataApprovazioneDocumenti);
      return businessDaysBetween(inizio, approvato);
    })
    .filter((v) => v != null && v >= 0);

  // Lead time medio valutazione Ditta = tempo che la Ditta impiega ad approvare il preventivo,
  // espresso in giorni lavorativi. Intervallo: dataInizioValutazioneDitta (settata all'approvazione
  // documenti E azzerata a ogni "aggiorna preventivo" del fornitore dopo rifiuto Ditta) →
  // dataFine (Ditta approva il preventivo e chiude la pratica).
  // Considera solo pratiche CHIUSE con dataInizioValutazioneDitta settata: i cicli di rifiuto/
  // rielaborazione non inquinano la metrica perché il timer viene resettato ogni round.
  const leadTimesDitta = practices
    .filter((p) => p.dataFine && p.dataInizioValutazioneDitta)
    .map((p) => {
      const inizioDitta = parseDateField(p.dataInizioValutazioneDitta);
      const fine = parseDateField(p.dataFine);
      return businessDaysBetween(inizioDitta, fine);
    })
    .filter((v) => v != null && v >= 0);

  // SLA Ditta: conteggio pratiche con tempo di gestione Ditta > 7 gg lavorativi.
  // Stesso perimetro di leadTimeDittaMedio (solo chiuse con dataInizioValutazioneDitta).
  const SLA_DITTA_SOGLIA_GG = 7;
  const slaDittaSuperato = leadTimesDitta.filter((d) => d > SLA_DITTA_SOGLIA_GG).length;

  const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);

  const importoValues = practices.map((p) => p.importo).filter((v) => v != null && v !== 0);
  const importoTotale = importoValues.length ? importoValues.reduce((a, b) => a + b, 0) : null;
  const importoMedio = avg(importoValues);

  // Rifiuti medi per pratica: somma dei contatori / numero pratiche.
  // numeroErroriDocumenti → rifiuti TECNICI (documenti non approvati da HQ/Fastweb).
  // numeroErroriPreventivo → rifiuti AMMINISTRATIVI (preventivo non approvato da Ditta).
  // Ogni rifiuto conta come errore distinto (scelta Fastweb); la media è calcolata sulle pratiche
  // filtrate attualmente visibili. Pratiche legacy senza contatori contribuiscono 0 al numeratore.
  // Null se nessuna pratica filtrata (evita divisione per zero).
  const totalRifiutiTecnici = practices.reduce((sum, p) => sum + (p.numeroErroriDocumenti || 0), 0);
  const totalRifiutiAmministrativi = practices.reduce((sum, p) => sum + (p.numeroErroriPreventivo || 0), 0);
  const rifiutiTecniciMedi = practices.length ? totalRifiutiTecnici / practices.length : null;
  const rifiutiAmministrativiMedi = practices.length ? totalRifiutiAmministrativi / practices.length : null;

  // Tempo medio di rilavorazione dello studio = media PER CICLO dei giorni lavorativi
  // che lo studio impiega a reagire a una bocciatura (documenti + preventivo).
  // Accumulatori scritti solo dalla Cloud Function onStudioRework alla chiusura di ogni
  // ciclo (rifiuto → ri-sottomissione). Legacy/cicli aperti = non contribuiscono.
  const totalTempoRilavorazione = practices.reduce(
    (sum, p) => sum + (p.tempoRilavorazioneDocGG || 0) + (p.tempoRilavorazionePreventivoGG || 0), 0);
  const totalCicliRilavorazione = practices.reduce(
    (sum, p) => sum + (p.numeroRilavorazioniDoc || 0) + (p.numeroRilavorazioniPreventivo || 0), 0);
  const rilavorazioneStudioMedia = totalCicliRilavorazione > 0
    ? totalTempoRilavorazione / totalCicliRilavorazione : null;

  return {
    total: practices.length,
    totalNonAvviate: nonAvviate.length,
    totalInCorso: inCorso.length,
    totalChiuse: chiuse.length,
    totalRifiutate: rifiutate.length,
    totalDaValutare: daValutare.length,
    totalInRielaborazione: inRielaborazione.length,
    leadTimeMedio: avg(leadTimes),
    leadTimeDittaMedio: avg(leadTimesDitta),
    slaDittaSuperato,
    importoTotale,
    importoMedio,
    rifiutiTecniciMedi,
    rifiutiAmministrativiMedi,
    rilavorazioneStudioMedia,
  };
}

/**
 * Ritorna i top N valori per un campo specifico.
 */
export function topNByField(practices, field, n = 5) {
  const counts = {};
  practices.forEach((p) => {
    const val = p[field];
    if (val) counts[val] = (counts[val] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, count]) => ({ name, count }));
}
